"use client";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
	faBook,
	faCalendarAlt,
	faChalkboardTeacher,
	faEnvelope,
	faGamepad,
	faGraduationCap,
	faLanguage,
	faMapMarkerAlt,
	faStar,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

const LearnEnglishPage = () => {
	const [showCopyNotification, setShowCopyNotification] = useState(false);

	const englishFeatures = [
		{
			id: "instructor",
			title: "Tu Máster",
			icon: faChalkboardTeacher,
			description: {
				paragraphs: [
					<>
						<a
							href="/master-de-rol-profesional-madrid#bez"
							className="text-red-400 hover:text-red-300 underline"
						>
							Bez
						</a>{" "}
						es un angloparlante nativo de Gran Bretaña, con veinticinco años de experiencia dirigiendo juegos
						de rol. Cuando no está jugando, su tiempo se divide entre la{" "}
						<a href="http://www.games4geeks.com" className="text-red-400 hover:text-red-300 underline">
							creación de sus propios juegos
						</a>
						, y dar clases de conversación en inglés.
					</>,
					<>
						¡Así que puedes estar seguro de que sabe cómo ayudar a la gente con su inglés y cómo ser un Máster
						de calidad! Puedes saber más sobre él y sus aficiones en su blog sobre todas las cosas frikis de
						Madrid,{" "}
						<a href="http://geekmadrid.com/" className="text-red-400 hover:text-red-300 underline">
							Geek Madrid
						</a>
						.
					</>,
				],
			},
			benefits: [
				"Profesor nativo de Inglaterra",
				"25 años de experiencia como director de juegos",
				"Experiencia en enseñanza de inglés conversacional",
				"Diseñador de juegos profesional",
				"Experto en cultura geek y friki",
			],
			image: "/images/aprende-ingles-master.webp",
			color: "border-red-500",
			accentColor: "bg-red-600",
			textColor: "text-red-400",
		},
		{
			id: "english",
			title: "Tu Inglés",
			icon: faLanguage,
			description: {
				paragraphs: [
					<>
						Mientras tu nivel de inglés sea suficiente para mantener una conversación simple, esta oferta es
						para ti. Tu Máster tendrá toda la paciencia que haga falta, y estará encantado de explicarte
						cualquier cosa que no entiendas, ya sea sobre el vocabulario, la gramática, o el propio juego.
					</>,
					<>
						Aprender un idioma no tiene que ser aburrido. Al combinarlo con la diversión de los juegos de rol,
						las palabras y frases nuevas se asimilan de forma natural y duradera. La presión de hablar
						correctamente desaparece cuando estás concentrado en derrotar a ese dragón o resolver el misterio
						del antiguo templo.
					</>,
				],
			},
			features: [
				"Aprende vocabulario específico de tus temas favoritos",
				"Practica en un entorno relajado y sin presiones",
				"Mejora tu fluidez mientras te diviertes",
				"Recibe correcciones naturales durante el juego",
				"Desarrolla tu confianza al hablar inglés",
			],
			image: "/images/aprende-ingles-conversation.webp",
			color: "border-blue-500",
			accentColor: "bg-blue-600",
			textColor: "text-blue-400",
		},
		{
			id: "game",
			title: "Tu Juego",
			icon: faGamepad,
			description: {
				paragraphs: [
					<>
						No importa que hayas estado jugando durante años, o que seas un novato total que nunca ha tocado
						un juego de rol, podemos proporcionarte una gran experiencia.
					</>,
					<>
						Échale un vistazo a todos{" "}
						<a href="/sistemas/" className="text-red-400 hover:text-red-300 underline">
							nuestros sistemas
						</a>
						, y encuentra el que más te guste. Y si no tenemos tu sistema favorito, siempre{" "}
						<a
							href="mailto:somos@losamosdelcalabozo.com?subject=Queridos Amos del Calabozo&"
							className="text-red-400 hover:text-red-300 underline"
						>
							puedes escribirnos
						</a>{" "}
						y hacernos saber el tipo de aventuras y escenarios que te atraen, y podemos adaptar las cosas a
						tus gustos.
					</>,
					<>
						Si eres un novato, no hay mejor manera de adentrarse en los juegos de rol que con una de nuestras{" "}
						<a href="/category/aventuras/" className="text-red-400 hover:text-red-300 underline">
							aventuras iniciáticas
						</a>
						; ¡todas ellas pueden ser jugadas en inglés! Y solo para las partidas en inglés, también existe la
						opción de hacerlo online.
					</>,
				],
			},
			features: [
				"Juegos adaptados a tu nivel de inglés y experiencia",
				"Amplia variedad de sistemas y ambientaciones",
				"Aventuras personalizables según tus intereses",
				"Opción de jugar presencialmente o en línea",
				"Material didáctico complementario disponible",
			],
			image: "/images/aprende-ingles-game.webp",
			color: "border-red-500",
			accentColor: "bg-red-600",
			textColor: "text-red-400",
		},
	];

	const benefits = [
		{
			icon: faLanguage,
			title: "Inmersión Natural",
			description:
				"Aprende inglés de forma orgánica mientras te diviertes resolviendo enigmas y enfrentándote a desafíos épicos.",
		},
		{
			icon: faUsers,
			title: "Entorno Social",
			description: "Practica con otros jugadores en un ambiente colaborativo y sin presiones académicas.",
		},
		{
			icon: faBook,
			title: "Vocabulario Específico",
			description: "Adquiere terminología especializada en los temas que realmente te interesan.",
		},
		{
			icon: faGraduationCap,
			title: "Aprendizaje Práctico",
			description: "Usa el idioma de forma práctica e inmediata, mejorando tu retención y comprensión.",
		},
	];

	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/aprende-ingles.webp')" }}
			>
				<div className="absolute inset-0 bg-black/70"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">Aprende Inglés</span>
					</motion.h1>

					<motion.p
						className="text-2xl text-gray-300 max-w-3xl mx-auto text-center mb-8"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="text-red-400">
							Mejora tu nivel mientras te diviertes
							<br />
							<span className="text-xl italic text-gray-300">
								¿Quieres mejorar tu inglés, o practicar lo que ya sabes de una manera divertida y en una
								atmósfera relajada? ¿Estás cansado de probar los mismos métodos aburridos una y otra vez? Los
								Amos del Calabozo tienen exactamente lo que buscas.
							</span>
						</strong>
					</motion.p>

					<motion.div
						className="text-center"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.6, duration: 0.6 }}
					>
						<p className="text-xl text-gray-200 max-w-2xl mx-auto mb-6">
							Olvídate de los libros de texto y las clases monótonas. En{" "}
							<span className="font-amarante text-red-400">Los Amos del Calabozo</span> aprenderás inglés
							mientras vives aventuras épicas a través de juegos de rol.
						</p>
						<p className="text-lg text-white bg-red-800/50 rounded-lg py-2 px-4 inline-block">
							Ahora ya no tienes excusa para no practicar inglés, ¡así que elige tu aventura y a jugar!
						</p>
					</motion.div>
				</div>
			</section>

			{/* Benefits Overview */}
			<section className="py-12 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faStar} className="mr-3 text-red-500" />
						¿Por qué aprender inglés con juegos de rol?
					</motion.h2>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
						{benefits.map((benefit, index) => (
							<motion.div
								key={index}
								className="bg-gray-800 rounded-lg p-6 flex items-start gap-4"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ delay: index * 0.1, duration: 0.5 }}
							>
								<div className="bg-red-600 p-3 rounded-full flex-shrink-0">
									<FontAwesomeIcon icon={benefit.icon} className="text-white text-xl" />
								</div>
								<div>
									<h3 className="text-xl font-amarante text-red-400 mb-2">{benefit.title}</h3>
									<p className="text-gray-300">{benefit.description}</p>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* Main Features Section */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faLanguage} className="mr-3 text-red-500" />
						Aprende inglés de una forma diferente
					</motion.h2>

					<div className="flex flex-col gap-16">
						{englishFeatures.map((feature, index) => (
							<motion.div
								key={feature.id}
								className={`bg-gray-800 rounded-lg overflow-hidden border-l-4 ${feature.color}`}
								initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true }}
								transition={{ delay: index * 0.2, duration: 0.6 }}
							>
								<div className="p-8">
									<div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
										<div
											className={`w-20 h-20 ${feature.accentColor} rounded-full flex items-center justify-center flex-shrink-0 shadow-lg`}
										>
											<FontAwesomeIcon icon={feature.icon} className="text-white text-3xl" />
										</div>
										<div>
											<h3 className={`text-3xl font-amarante headerFont ${feature.textColor}`}>
												{feature.title}
											</h3>
										</div>
									</div>

									<div className="space-y-6">
										{feature.description.paragraphs.map((paragraph, idx) => (
											<p key={idx} className="text-gray-300">
												{paragraph}
											</p>
										))}

										{feature.benefits && (
											<div className="mt-6">
												<h4 className="text-xl font-amarante text-red-300 mb-4">
													<FontAwesomeIcon icon={faStar} className="mr-2" />
													Ventajas:
												</h4>
												<ul className="list-none grid grid-cols-1 md:grid-cols-2 gap-3">
													{feature.benefits.map((benefit, idx) => (
														<li key={idx} className="text-gray-300 flex items-start">
															<span className="text-red-400 mr-2">•</span> {benefit}
														</li>
													))}
												</ul>
											</div>
										)}

										{feature.features && (
											<div className="mt-6">
												<h4 className="text-xl font-amarante text-red-300 mb-4">
													<FontAwesomeIcon icon={faStar} className="mr-2" />
													Características:
												</h4>
												<ul className="list-none grid grid-cols-1 md:grid-cols-2 gap-3">
													{feature.features.map((feat, idx) => (
														<li key={idx} className="text-gray-300 flex items-start">
															<span className="text-red-400 mr-2">•</span> {feat}
														</li>
													))}
												</ul>
											</div>
										)}
									</div>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* Format & Pricing */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faCalendarAlt} className="mr-3 text-red-500" />
						Modalidades y Precios
					</motion.h2>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
						<motion.div
							className="bg-gray-800 rounded-lg p-6 border-l-4 border-red-600"
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
						>
							<h3 className="text-2xl font-amarante headerFont text-red-400 mb-4">Sesiones Individuales</h3>
							<ul className="space-y-3 mb-6">
								<li className="flex items-start">
									<span className="text-red-400 mr-2 mt-1">•</span>
									<span className="text-gray-300">
										Sesiones personalizadas adaptadas a tu nivel y tus intereses
									</span>
								</li>
								<li className="flex items-start">
									<span className="text-red-400 mr-2 mt-1">•</span>
									<span className="text-gray-300">Flexibilidad horaria</span>
								</li>
								<li className="flex items-start">
									<span className="text-red-400 mr-2 mt-1">•</span>
									<span className="text-gray-300">Atención completamente individualizada</span>
								</li>
								<li className="flex items-start">
									<span className="text-red-400 mr-2 mt-1">•</span>
									<span className="text-gray-300">Disponible presencial y online</span>
								</li>
							</ul>
							<div className="text-right">
								<span className="text-2xl font-bold text-white">Desde 25€/hora</span>
							</div>
						</motion.div>

						<motion.div
							className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-600"
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 0.1 }}
						>
							<h3 className="text-2xl font-amarante headerFont text-blue-400 mb-4">Sesiones Grupales</h3>
							<ul className="space-y-3 mb-6">
								<li className="flex items-start">
									<span className="text-blue-400 mr-2 mt-1">•</span>
									<span className="text-gray-300">Grupos reducidos (2-5 personas)</span>
								</li>
								<li className="flex items-start">
									<span className="text-blue-400 mr-2 mt-1">•</span>
									<span className="text-gray-300">Ambiente social y colaborativo</span>
								</li>
								<li className="flex items-start">
									<span className="text-blue-400 mr-2 mt-1">•</span>
									<span className="text-gray-300">Campañas continuadas</span>
								</li>
								<li className="flex items-start">
									<span className="text-blue-400 mr-2 mt-1">•</span>
									<span className="text-gray-300">Disponible presencial y online</span>
								</li>
							</ul>
							<div className="text-right">
								<span className="text-2xl font-bold text-white">Desde 15€/hora por persona</span>
							</div>
						</motion.div>
					</div>

					<motion.div
						className="mt-8 bg-gray-800 rounded-lg p-6 border-l-4 border-red-600"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ delay: 0.2 }}
					>
						<div className="flex items-center gap-4 mb-4">
							<div className="bg-red-600 p-3 rounded-full">
								<FontAwesomeIcon icon={faMapMarkerAlt} className="text-white text-xl" />
							</div>
							<h3 className="text-2xl font-amarante headerFont text-red-400">Ubicación</h3>
						</div>
						<p className="text-gray-300 mb-4">
							Las sesiones presenciales se realizan en nuestra sala privada en Madrid o podemos desplazarnos a
							tu ubicación dentro de la Comunidad de Madrid (puede aplicarse un cargo adicional por
							desplazamiento).
						</p>
						<p className="text-gray-300">
							Las sesiones online se realizan a través de plataformas como Roll20, Discord o la que prefieras,
							permitiéndote conectar desde cualquier parte del mundo.
						</p>
					</motion.div>
				</div>
			</section>

			{/* Final CTA */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="bg-gray-800 rounded-lg p-8 text-center border-4 border-red-700"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<motion.div
							whileHover={{ scale: 1.05 }}
							className="mx-auto w-24 h-24 bg-red-600 rounded-full flex items-center justify-center mb-6"
						>
							<FontAwesomeIcon icon={faGraduationCap} className="text-white text-4xl" />
						</motion.div>
						<h2 className="text-3xl font-amarante headerFont text-red-400 mb-4">
							¡La aventura lingüística te espera!
						</h2>
						<p className="text-gray-300 text-xl mb-8">
							Ahora ya no tienes excusa para no practicar inglés. Con Los Amos del Calabozo, aprenderás
							mientras vives aventuras épicas.{" "}
							<a href="/category/aventuras/" className="text-red-400 hover:text-red-300 underline">
								Elige tu aventura
							</a>
							, ¡y a jugar!
						</p>
						<motion.div
							className="flex flex-col md:flex-row items-center justify-center gap-4"
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
						>
							<motion.a
								href="mailto:somos@losamosdelcalabozo.com?subject=Consulta sobre Clases de Inglés"
								className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.1,
									boxShadow: "0 0 15px #3b82f6",
									backgroundColor: "white",
									color: "black",
									fontWeight: "900",
									fontStyle: "italic",
								}}
								whileTap={{ scale: 0.98 }}
							>
								<FontAwesomeIcon icon={faEnvelope} className="mr-2" />
								Reservar mi Primera Clase
							</motion.a>

							<motion.a
								href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, me gustaría obtener más información sobre vuestras clases de inglés con juegos de rol. ¿Podéis ayudarme?")}`}
								className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.05,
									boxShadow: "0 0 15px #22c55e",
								}}
								whileTap={{ scale: 0.98 }}
								target="_blank"
								rel="noopener noreferrer"
							>
								<FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
								Consultar por WhatsApp
							</motion.a>
						</motion.div>
					</motion.div>
				</div>
			</section>
			{/* Email  Contact Methods */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					{/* Email Address Display */}
					<motion.div
						className="mt-8 sm:mt-10 md:mt-12 bg-gray-800 rounded-lg p-4 sm:p-6 md:p-8 text-center relative"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.8, duration: 0.6 }}
					>
						<h3 className="text-xl sm:text-2xl font-amarante headerFont text-red-400 mb-4">
							Nuestro Pergamino Oficial
						</h3>
						<motion.div
							className="text-lg sm:text-2xl md:text-4xl font-bold text-white bg-gray-700 py-3 px-2 sm:px-4 rounded-lg select-all cursor-pointer"
							whileHover={{
								backgroundColor: "#3b82f6",
								color: "white",
								transition: { duration: 0.3 },
							}}
							onClick={() => {
								navigator.clipboard.writeText("somos@losamosdelcalabozo.com");
								setShowCopyNotification(true);
								setTimeout(() => setShowCopyNotification(false), 2000);
							}}
						>
							somos@losamosdelcalabozo.com
						</motion.div>
						<p className="text-gray-400 mt-2 text-xs sm:text-sm italic">
							Haz click para copiar a tu grimorio de hechizos (portapapeles)
						</p>
						<AnimatePresence>
							{showCopyNotification && (
								<motion.div
									className="bg-green-600 text-white text-sm py-2 px-4 rounded-md absolute left-1/2 transform -translate-x-1/2 mt-2"
									initial={{ opacity: 0, y: -10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0 }}
								>
									Email copiado al portapapeles
								</motion.div>
							)}
						</AnimatePresence>
					</motion.div>
				</div>
			</section>

			{/* Easter Egg */}
			<section className="py-4 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="text-center opacity-30 hover:opacity-100 transition-opacity cursor-pointer"
						whileHover={{ scale: 1.01 }}
						whileTap={{ scale: 0.99 }}
					>
						<p className="text-sm text-gray-500 italic">
							Algunos afirman que dominar el idioma de los antiguos bardos —el inglés— permite leer grimorios
							olvidados, negociar con dragones diplomáticos y descifrar mapas del tesoro escritos en lenguas
							perdidas... ¿Te atreves a intentarlo?
						</p>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default LearnEnglishPage; /**/
